'use client'
import React, { useMemo, useRef, useState } from 'react'
import NextLink from 'next/link'
import { Link as AscendLink } from '@achieve/ascend'
import { Typography } from 'components/Typography'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { ROUTE_EXCEPT_NOFOLLOW, ROUTE_ASSET_EXCEPT } from 'constants/navigation'
import { getProductAndAddDefaultUtmsToLink } from 'utils/queryManipulation'
import Cookies from 'js-cookie'
import { disableClientSideRoutes } from 'constants/experiments'
import {
  preserveQueryParams,
  sanitizeUrlParams,
  moveAnchorRight,
  addTrailingSlashToHref,
} from 'utils/shared'
import { FdrPage } from 'utils/environmentConfig/constants'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import logger from '@achieve/cx-logger'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'

const LinkWrapper = React.forwardRef(({ children, refAs, ...props }, ref) => {
  if (refAs) {
    props[refAs] = ref
  }

  const hasPropsToMerge = Object.keys(props).length > 0
  const isValidElement = React.isValidElement(children)

  if (typeof children === 'string') {
    return <>{children}</>
  }

  if (isValidElement && hasPropsToMerge) {
    return <>{React.cloneElement(children, props)}</>
  }

  // RSC children passed into client components are not always clone-safe in production.
  return <>{children}</>
})

LinkWrapper.displayName = 'LinkWrapper'

/**
 *
 * @param {string} href original href string
 * @param {ReactNode} children child components wrapped with AchieveLink
 * @param {boolean} withNextLink if true use next/link, default use AscendLink
 * @param {RefObject} refAs external ref to be used in the link child
 * @returns {ReactElement} returns link wrapped child component with forwarded
 *                         ref, passed props and transformed/memoized href
 *                         with original query params preserved
 */
const AchieveLink = ({
  href,
  children,
  withNextLink = false,
  refAs,
  track,
  onClick,
  rel,
  noLink,
  noPreserveParams,
  queryParamsToPreserve,
  ...restProps
}) => {
  const trackRefApiResponse = useRef()
  const continuePropagation = useRef()
  const [userHasInteracted, setUserHasInteracted] = useState(false)
  const { waitForNavigation } = useTriggerEvents()

  const { query } = useAppRouter()

  const [trackAsConversion] = useState(false)

  const visitId = useMemo(() => Cookies.get('eh_visit_id'), [])

  // On the FDR site we need to mirror the `trailingSlash: true` behavior from
  // `next.config.mjs` for internal links.
  const isFdrSite = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT === FdrPage

  const getUtmEnhancedHref = () => {
    let calcHref = href
    if (!noPreserveParams) {
      calcHref = getProductAndAddDefaultUtmsToLink(
        preserveQueryParams(href, query, visitId, queryParamsToPreserve)
      )
    }
    calcHref = moveAnchorRight(calcHref)

    let next = sanitizeUrlParams(calcHref)
    if (isFdrSite) {
      next = addTrailingSlashToHref(next)
    }
    return next
  }

  let linkRel = rel ?? null
  const noFollowExceptionRoute = ROUTE_EXCEPT_NOFOLLOW.findIndex((exception) =>
    String(`${href}`).toLowerCase().includes(exception)
  )
  if (
    String(`${href}`).toLowerCase().includes('http') &&
    noFollowExceptionRoute == -1 &&
    !String(`${href}`).toLowerCase().includes(ROUTE_ASSET_EXCEPT)
  ) {
    if (!String(`${linkRel}`).toLowerCase().includes('nofollow')) {
      linkRel = `${rel ? `${rel} ` : ''}nofollow`
    }
    restProps = { ...restProps, target: '_blank' }
  }
  if (disableClientSideRoutes.includes(href)) {
    onClick = (e) => {
      e?.preventDefault()
      const utmEnhancedHref = getUtmEnhancedHref()
      window.location.href = utmEnhancedHref
    }
  }
  let LinkComponent = withNextLink && !noLink ? NextLink : AscendLink
  const nextProps = withNextLink && !noLink ? { passHref: true } : {}

  const handleClick = (alreadyTracked) => {
    if (track && !alreadyTracked) {
      trackRefApiResponse.current.click()
    }

    if (onClick) {
      onClick()
    }
  }

  let linkNoCrawl = false
  if (typeof href === 'string' && href?.substring(0, 15).includes('legal')) {
    linkNoCrawl = true
    LinkComponent = Typography
  }

  let component
  if (linkNoCrawl) {
    component = 'div'
  } else if (href && !noLink) {
    component = 'a'
  } else {
    component = 'span'
  }

  // Extract base URL (without query params) from href
  const baseHref = useMemo(() => {
    if (!href || typeof href !== 'string') {
      return href
    }

    let computed
    try {
      const isFullUrl = href.startsWith('http://') || href.startsWith('https://')

      if (isFullUrl) {
        const url = new URL(href)
        computed = `${url.origin}${url.pathname}${url.hash}`
      } else {
        const [pathWithHash] = href.split('?')
        computed = pathWithHash
      }
    } catch {
      computed = href
    }

    return isFdrSite ? addTrailingSlashToHref(computed) : computed
  }, [href, isFdrSite])

  let extraProps = {}
  if (noLink || linkNoCrawl) {
    extraProps = {}
  } else {
    if (!userHasInteracted) {
      extraProps = {
        href: baseHref,
      }
    } else {
      extraProps = {
        href: getUtmEnhancedHref(),
      }
    }
  }

  const handleLinkClick = async (e) => {
    // Native protocol links (tel:, mailto:, sms:) — let the browser handle the
    // navigation natively so the rendered <a href="tel:..."> works for screen
    // readers, right-click "Copy", cmd/ctrl/middle click, and crawlers. We only
    // need to fire the analytics tracking here.
    if (typeof href === 'string' && /^(tel:|mailto:|sms:)/i.test(href)) {
      handleClick()
      return
    }
    const utmEnhancedHref = getUtmEnhancedHref()
    // Check for modifier keys (cmd+click, ctrl+click, shift+click, middle-click)
    const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1
    if (href && href.indexOf('http') !== -1 && !linkNoCrawl) {
      if (e.nativeEvent.isTrusted) {
        e.preventDefault()
        e.stopPropagation()
        await waitForNavigation({
          ...track,
        })
          .catch(() => {
            logger.log('Event timed out')
            logger.log({ track })
          })
          .finally(() => {
            if (process.env.NODE_ENV !== 'test') {
              if (restProps.target === '_blank' || isModifiedClick) {
                window.open(utmEnhancedHref, '_blank')
              } else {
                window.location.href = utmEnhancedHref
              }
            }
          })
      } else {
        handleClick(true)
      }
    } else if (linkNoCrawl) {
      window.open(utmEnhancedHref, '_self')
    } else if (href && !noLink) {
      e.preventDefault()
      if (restProps.target === '_blank' || isModifiedClick) {
        window.open(utmEnhancedHref, '_blank')
      } else {
        window.location.href = utmEnhancedHref
      }
      handleClick()
    } else {
      handleClick()
    }
  }

  const handleAuxClick = (e) => {
    if (e.button === 1) {
      handleLinkClick(e)
    }
  }

  const handleContextMenu = () => {
    setUserHasInteracted(true)
  }

  return (
    <>
      <LinkComponent
        component={component}
        {...extraProps}
        rel={linkRel}
        ref={linkNoCrawl ? null : continuePropagation}
        underline="hover"
        onClick={handleLinkClick}
        onAuxClick={handleAuxClick}
        onContextMenu={handleContextMenu}
        {...restProps}
        {...(withNextLink && !noLink && !linkNoCrawl ? { prefetch: false } : {})}
        {...(linkNoCrawl ? {} : nextProps)}
      >
        {children?.type === 'a' ? children : <LinkWrapper refAs={refAs}>{children}</LinkWrapper>}
      </LinkComponent>
      <TrackingWrapper
        track={{ href: href, ...track }}
        onClick={onClick}
        withNextLink={withNextLink}
        trackAsConversion={trackAsConversion}
        className="hidden!"
      >
        <span ref={trackRefApiResponse}></span>
      </TrackingWrapper>
    </>
  )
}

export { AchieveLink }
